import * as config from '../constants.js';
import { getRarityDropChance, getRarityQuantityRange, addLoot, addLogEntry, addRaidMessage,
  getRoomFaction, getBlueprintsForRoom, getFactionSpecificMaterials, getItemsByType, regionTierToRarity,
  applyCreditsScaling, pickWeightedItem
}  from './raidUtil.js'
import { addNotification } from "./notifications.js";
import { getItemName, getSkillDisplayName, getTechniqueName, getTechniqueSkill, findItem, getAllItems, getEffect, getTechniqueIcon } from "./util.js"
import { isSynergyUnlocked, getSkillArtifactBonus, getUtilityArtifactBonus } from "./character-core.js";
import { debug, isDebug, CH } from './debugLog.js';
import { blackoutModifier } from './blackout-core.js';
import { emitRaidFx } from './raidFx-core.js';

// ============================================================================
// Event Initialization
// ============================================================================

/**
 * Initialize all room events when entering a gate/hazard/water room
 * Sets up event structures and schedules appropriate events for room type
 * @param {Object} room - Room object with type, baseDuration, primary, support skills
 * @param {Object} state - gameState object
 */
export function initializeRoomEvents(room, state) {
  if (!state.currentRaid?.raidInProgress) {
    return;
  }

  // Create unified events object if it doesn't exist
  if (!state.currentRaid.raidInProgress.events) {
    state.currentRaid.raidInProgress.events = {
      skillCheckEvents: [],
      bonusLootEvent: null,
      fishingEvent: null
    };
  }

  // Initialize based on room type
  if (room.type === 'water') {
    // Water room: initialize fishing only
    initializeFishingEvent(room, state);
  } else if (room.type === 'gate' || room.type === 'hazard') {
    // Gate/hazard room: initialize skill-check events + conditional bonus loot
    scheduleSkillCheckEvents(room, state);
    scheduleConditionalBonusLoot(room, state);
  }

  debug(CH.events, `Events initialized for ${room.type} room`);
}

/**
 * Display the problem statement when a skill-check event starts
 * Shows the title and problem description, keeping narrative separate from resolution
 * @param {Object} eventData - Event data with event definition
 * @param {Number} roomIndex - Path step index, for message threading
 * @param {Object} state - gameState object
 * @param {Array} team - Current raid team
 */
export function logEventProblem(eventData, roomIndex, state, team) {
  const event = eventData.event;
  if (!event?.variants || event.variants.length === 0) return;

  // The SAME variant that will resolve this event. This used to hardcode
  // variants[0], so a team carrying the right cross-faction tool was told about
  // one problem and then read a resolution to a different one — and the 90
  // problem statements on the synergy variants were never shown to anyone.
  const { variant } = resolveEventVariant(event, state, team);
  if (!variant) return;

  const problemText = interpolateTechNames(variant.problem, event, variant);
  const skillName = getSkillDisplayName(event.relatedSkillId);
  const primaryTechniqueName = getTechniqueName(event.relatedTechniqueId);
  const techniqueIcon = getTechniqueIcon(event.relatedTechniqueId);  
  addRaidMessage(`⚠️ ${skillName}: ${techniqueIcon} ${primaryTechniqueName} Needed <div class="event-problem-text">${problemText}</div>`,
    'warning',
    {
      stepIndex: roomIndex,
      notificationMessage: `⚠️ ${skillName} - ${variant.title}` // Show toast notification
    }
  );

  debug(CH.events, `Event problem triggered: ${variant.title}`);
}

/**
 * Count room events before cleanup (for tracking/analytics)
 * Returns the number of skill check events and whether bonus loot occurred
 * @param {Object} state - gameState object
 * @returns {Object} { skillCheckCount: number, hasBonusLoot: boolean }
 */
export function countRoomEvents(state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return { skillCheckCount: 0, hasBonusLoot: false };
  }

  const events = state.currentRaid.raidInProgress.events;
  const skillCheckCount = events.skillCheckEvents?.length || 0;
  const hasBonusLoot = !!events.bonusLootEvent;

  return { skillCheckCount, hasBonusLoot };
}

/**
 * Cascade multiplier for a specific tier and cascade step (rarity being rolled).
 * Config shape is per-tier objects ({ rare, epic, legendary, unique }); a plain
 * number is accepted as a flat multiplier for backward compatibility.
 */
export function getCascadeMultiplier(tier, rarity) {
  const tierMultipliers = config.progressionScaling?.bonusEventCascadeMultipliers?.[tier];
  if (tierMultipliers == null) return 1.0;
  if (typeof tierMultipliers === 'number') return tierMultipliers;
  return tierMultipliers[rarity] ?? 1.0;
}

/**
 * Effective per-room chance of receiving at least one item of each rarity,
 * assuming the room's events succeed. Combines every source the engine has:
 * - Direct skill-check loot: each of N events (hazard 1-2, gate 2-4; expected
 *   value used) rolls common/uncommon/rare at getRarityDropChance on success.
 * - The end-of-room bonus event: awards faction intel CREDITS, then cascades
 *   to items rare -> epic -> legendary -> unique, each step rolling that
 *   rarity's drop chance times the tier cascade multiplier.
 *
 * Used by the raid-planning loot summary so the displayed numbers reflect
 * what the engine actually rolls, regardless of mechanic.
 *
 * @param {number} tier - Raid tier (1-3)
 * @param {Object} room - Room object (type 'gate' or 'hazard')
 * @returns {Object} { common, uncommon, rare, epic, legendary, unique } probabilities 0-1
 */
export function getRoomRarityChances(tier, room) {
  const expectedEvents = room.type === 'hazard' ? 1.5 : 3; // midpoints of 1-2 and 2-4

  const directChance = (rarity) => {
    const perEvent = getRarityDropChance(rarity, tier, room.type);
    return 1 - Math.pow(1 - perEvent, expectedEvents);
  };
  const cascadeStep = (rarity) =>
    Math.min(getRarityDropChance(rarity, tier, room.type) * getCascadeMultiplier(tier, rarity), 1);

  // Bonus cascade chain probabilities (bonus event itself awards common outright)
  const bonusRare = cascadeStep('rare');
  const bonusEpic = bonusRare * cascadeStep('epic');
  const bonusLegendary = bonusEpic * cascadeStep('legendary');
  const bonusUnique = bonusLegendary * cascadeStep('unique');

  const combine = (direct, viaBonus) => 1 - (1 - direct) * (1 - viaBonus);

  return {
    common: directChance('common'), // bonus event awards intel credits, not commons
    uncommon: directChance('uncommon'), // not part of the cascade chain
    rare: combine(directChance('rare'), bonusRare),
    epic: bonusEpic,
    legendary: bonusLegendary,
    unique: bonusUnique,
  };
}

/**
 * Cleanup all room events when room completes
 * Removes active UI elements and resets event state
 * Called from processCurrentRoomCompletion()
 * @param {Object} state - gameState object
 */
export function cleanupRoomEvents(state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return;
  }

  const events = state.currentRaid.raidInProgress.events;

  // Cleanup skill-check events
  if (events.skillCheckEvents?.length > 0) {
    events.skillCheckEvents = [];
  }

  // Cleanup bonus loot event
  if (events.bonusLootEvent) {
    cleanupBonusLootEvent(state);
  }

  // Cleanup fishing event
  if (events.fishingEvent) {
    cleanupFishingEvent(state);
  }

}

/**
 * Get summary of events for current room (for UI/logging)
 * @param {Object} state - gameState object
 * @returns {Object} Event summary with counts and statuses
 */
export function getEventsSummary(state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return { skillCheckEvents: 0, bonusLoot: false, fishing: false };
  }

  const events = state.currentRaid.raidInProgress.events;
  const skillCheckSummary = (events.skillCheckEvents || []).reduce(
    (acc, e) => ({
      ...acc,
      [e.status]: (acc[e.status] || 0) + 1
    }),
    {}
  );

  return {
    skillCheckEvents: events.skillCheckEvents?.length || 0,
    skillCheckStatus: skillCheckSummary,
    bonusLoot: !!events.bonusLootEvent?.scheduled,
    fishing: events.fishingEvent?.active || false
  };
}

/**
 * Process all scheduled events for the current tick
 * Called from loopRaidProgression() loop each frame
 * Triggers scheduled events when their time arrives
 * @param {Object} state - gameState object
 * @param {Array} team - Current raid team for skill checks
 */
export function processScheduledEventsCore(state, team) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return;
  }

  const raid = state.currentRaid.raidInProgress;
  const currentTime = Date.now() - (raid.roomStartTime || Date.now());

  // Skill-check events run in two phases: 'start' shows the problem, 'end'
  // resolves it. Return at most ONE transition per tick, resolving a finished
  // event before ever starting a new one, and never starting one while another
  // is in progress. This serializes events so a new one cannot begin before the
  // previous has finished - the old code kept the LAST due transition from a
  // forEach and flipped every due event to 'started', so when an event's end
  // and a later event's start fell in the same tick (a frame gap, e.g. after the
  // tab was backgrounded) the start pre-empted the end and the events overlapped.
  if (raid.events.skillCheckEvents?.length > 0) {
    const events = raid.events.skillCheckEvents;

    // 1) Resolve the earliest event whose duration has elapsed.
    let toEnd = null;
    for (const e of events) {
      if (e.status === 'started' && currentTime >= e.scheduledTimeMs + e.thisEventDurationMs) {
        if (!toEnd || (e.scheduledTimeMs + e.thisEventDurationMs) < (toEnd.scheduledTimeMs + toEnd.thisEventDurationMs)) {
          toEnd = e;
        }
      }
    }
    if (toEnd) {
      return { eventLifeCycle: 'end', eventType: 'skillCheck', eventData: toEnd };
    }

    // 2) Start the earliest due event, but only when none is still in progress.
    const anyInProgress = events.some(e => e.status === 'started');
    if (!anyInProgress) {
      let toStart = null;
      for (const e of events) {
        if (e.status === 'pending' && currentTime >= e.scheduledTimeMs) {
          if (!toStart || e.scheduledTimeMs < toStart.scheduledTimeMs) toStart = e;
        }
      }
      if (toStart) {
        toStart.status = 'started';
        return { eventLifeCycle: 'start', eventType: 'skillCheck', eventData: toStart };
      }
    }
  }

  // Process bonus loot event (only if all skill checks passed)
  if (raid.events.bonusLootEvent) {
    const bonusEvent = raid.events.bonusLootEvent;
    if (
      bonusEvent.scheduled &&
      !bonusEvent.triggered &&
      shouldTriggerBonusLoot(state) &&
      currentTime >= bonusEvent.scheduledTimeMs
    ) {
      return { 
        eventLifeCycle: 'start',
        eventType: 'bonusLoot',
        eventData: null,
      };
      //spawnBonusLootEvent(state);
    }
  }

  // Process fishing event (water rooms only)
  if (raid.events.fishingEvent?.active) {
    const fishingEvent = raid.events.fishingEvent;
    if (
      !fishingEvent.targetActive &&
      Date.now() >= fishingEvent.nextFishTime
    ) {
      return { 
        eventLifeCycle: 'start',
        eventType: 'fishing',
        eventData: null,
      };
      //spawnFish(state);
    }
  }
}

// ============================================================================
// Skill-Check Events
// ============================================================================

/**
 * Get all available events for a room based on primary and support skills
 * @param {Object} room - Room object
 * @param {Object} state - gameState object
 * @returns {Array} Array of event definitions matching room skills
 */
// Memoized on the room's skills, which is all the pool depends on — `state`
// is declared for call-site symmetry and has never been read. Worth caching
// because pre-rolling now calls this once per room at grid generation, and the
// simulator builds up to three candidate grids per raid.
const eventPoolCache = new Map();

export function getEventsForRoom(room, state) {
  const key = `${room.primary}|${(room.support || []).join(',')}`;
  const cached = eventPoolCache.get(key);
  if (cached) return cached;

  const allEvents = [
    ...config.eventDefinitionsDominion,
    ...config.eventDefinitionsWilders,
    ...config.eventDefinitionsVeil
  ];

  // Build set of relevant skill IDs (primary + support)
  const relevantSkills = new Set([room.primary, ...(room.support || [])]);

  // Filter events matching any relevant skill
  const pool = allEvents.filter(event => relevantSkills.has(event.relatedSkillId || event.favoredSkill));
  eventPoolCache.set(key, pool);
  return pool;
}

/** Look an event definition back up by id (pre-rolled raids store ids, not objects). */
export function findEventById(id) {
  for (const list of [config.eventDefinitionsDominion, config.eventDefinitionsWilders, config.eventDefinitionsVeil]) {
    const found = list.find(e => e.id === id);
    if (found) return found;
  }
  return null;
}

/**
 * Decide, at grid-generation time, WHICH events each encounter room will run.
 *
 * The map needs this: a tile is drawn once when the grid is displayed, long
 * before the room is entered, so it can only depict an encounter that has
 * already been decided. Pinning the identities here also makes a raid's
 * encounters stable across a save/reload.
 *
 * Only the identities are pinned — NOT how many fire. The count depends on
 * `eventChanceMultiplier`, which a blackout contract can arm during planning,
 * i.e. after the grid exists. Freezing it here would quietly disable
 * Overclocked Grid, so the count roll stays at room entry and simply draws
 * from this list (see scheduleSkillCheckEvents).
 */
export function preRollGridEvents(grid) {
  for (const row of grid || []) {
    for (const room of row || []) {
      if (!room || (room.type !== 'gate' && room.type !== 'hazard')) continue;
      const pool = getEventsForRoom(room);
      if (!pool.length) continue;
      // the un-multiplied ceiling for this room type; a blackout raid that
      // wants more rolls the extras fresh
      const most = room.type === 'hazard' ? 2 : 4;
      const ids = [];
      let lastIdx = -1;
      for (let i = 0; i < most; i++) {
        let idx = Math.floor(Math.random() * pool.length);
        // no two in a row the same, when the pool is big enough to avoid it
        if (pool.length > 1) while (idx === lastIdx) idx = Math.floor(Math.random() * pool.length);
        ids.push(pool[idx].id);
        lastIdx = idx;
      }
      room.plannedEventIds = ids;
    }
  }
  return grid;
}

/**
 * Schedule skill-check events for the room
 * 1-2 for hazards, 2-4 for gates, semi-randomly spaced
 * @param {Object} room - Room object
 * @param {Object} state - gameState object
 */
export function scheduleSkillCheckEvents(room, state) {
  // Get available events for this room
  const availableEvents = getEventsForRoom(room, state);
  if (availableEvents.length === 0) {
    console.warn(`No events available for room with primary skill: ${room.primary}`);
    state.currentRaid.raidInProgress.events.skillCheckEvents = [];
    return;
  }

  // Randomly determine event count (1-2 for hazards, 2-4 for gates)
  const eventCountRange = room.type === 'hazard' ? [1, 2] : [2, 4];
  // Overclocked Grid doubles the RANGE, not the roll: doubling afterwards would
  // turn a 1 into a 2 and a 4 into an 8, compressing the low end and blowing
  // out the high one. Scaling the bounds keeps the distribution's shape.
  const rateMultiplier = blackoutModifier('eventChanceMultiplier', 1);
  const scaled = eventCountRange.map(n => Math.round(n * rateMultiplier));
  const eventCount = scaled[0] + Math.floor(Math.random() * (scaled[1] - scaled[0] + 1));

  // Take the events the grid already committed to (see preRollGridEvents), so
  // the encounter matches what the map has been showing since the grid was
  // drawn. A raid generated before pre-rolling existed — or one whose blackout
  // multiplier asks for more events than were pinned — rolls the remainder
  // here, which is also the whole of the old behaviour.
  const planned = (room.plannedEventIds || [])
    .map(findEventById)
    .filter(Boolean);
  const selectedEvents = planned.slice(0, eventCount);
  let lastEventIdx = selectedEvents.length
    ? availableEvents.indexOf(selectedEvents[selectedEvents.length - 1])
    : -1;
  while (selectedEvents.length < eventCount) {
    let thisEventIdx = Math.floor(Math.random() * availableEvents.length);
    // the anti-repeat guard used to roll a THIRD index for the push, so it
    // never actually prevented a repeat; the drawn index is the one used now
    if (availableEvents.length > 1) {
      while (thisEventIdx === lastEventIdx) {
        thisEventIdx = Math.floor(Math.random() * availableEvents.length);
      }
    }
    selectedEvents.push(availableEvents[thisEventIdx]);
    lastEventIdx = thisEventIdx;
  }

  // Calculate room duration in seconds
  const jitterPercent = 0.1; // ±10%
  const roomDurationSec = (room.baseDuration > 0 ? room.baseDuration - 15 : 0) * (1 - (jitterPercent * eventCount));  //account for bonus event in last 15sec

  // Schedule events at semi-random intervals (2 events/min ≈ 30s spacing)
  // Evenly space them across room duration with ±10% jitter
  const eventDurationMs = (roomDurationSec / eventCount) * 1000;
  
  let prevEventEndTimeMs = -1;
  const scheduledEvents = selectedEvents.map((event, index) => {
    let thisEventDurationMs = eventDurationMs;

    const jitterMs = (Math.random() * thisEventDurationMs * jitterPercent);
    let scheduledTimeMs = (index * thisEventDurationMs) + jitterMs;
    
    if (prevEventEndTimeMs > -1 && scheduledTimeMs < prevEventEndTimeMs) {
      scheduledTimeMs = prevEventEndTimeMs + 1000;
    }
    
    if (scheduledTimeMs + thisEventDurationMs > (room.baseDuration > 0 ? room.baseDuration - 15 : 0)*1000) {
      thisEventDurationMs = thisEventDurationMs - (scheduledTimeMs - (room.baseDuration > 0 ? room.baseDuration - 15 : 0)*1000);
    }

    prevEventEndTimeMs = scheduledTimeMs + thisEventDurationMs;

    return {
      event,
      scheduledTimeMs, //ms until event time
      thisEventDurationMs,
      status: 'pending', // pending, started, completed, failed
      index
    };
  });

  state.currentRaid.raidInProgress.events.skillCheckEvents = scheduledEvents;
  state.currentRaid.raidInProgress.hasSkillEventFailure = false;

  debug(CH.events, `Scheduled ${eventCount} skill-check events for room`, {
    roomType: room.type,
    eventCount,
    baseDuration: room.baseDuration,
    scheduledTimes: scheduledEvents.map(e => (e.scheduledTimeMs / 1000).toFixed(1) + 's')
  });
}

/**
 * Process a skill-check event when it fires
 * Main orchestration: select variant → resolve outcome → apply loot/damage → show message
 * @param {Object} eventData - Event data { event, scheduledTimeMs, status, index }
 * @param {Object} state - gameState object
 * @param {Object} room - Current room object
 * @param {Array} team - Current raid team
 * @returns {Object} Event result { success, variant, loot, damage, casualties }
 */
export function processSkillCheckEvent(eventData, state, room, team) {
  const { event } = eventData;
  const currentRoomIndex = state.currentRaid.raidInProgress.currentRoomIndex;
  const tier = state.currentRaid.currentRaidGrid.tier;

  // Step 1: Get team skill levels
  const teamSkills = {};
  team.forEach(member => {
    Object.entries(member.skills || {}).forEach(([skillId, level]) => {
      teamSkills[skillId] = Math.max(teamSkills[skillId] || 0, level);
    });
  });

  // Step 2 + 3: Select the best available variant (synergy variants need the
  // right cross-faction tool in someone's kit; priority variant[1] > [2] >
  // primary). Shared with logEventProblem so the problem the player was shown
  // and the outcome they get are the same scenario — see resolveEventVariant.
  // The +10% device success bonus below is the gear layer - never gated.
  const { variant, availableTechniques } = resolveEventVariant(event, state, team);
  if (!variant) {
    console.warn(`Could not select variant for event ${event.id}`);
    return { success: false, variant: null, loot: [], damage: 0, casualties: [] };
  }

  // Step 4: Resolve outcome. Flat chance bonuses stack inside the 97% clamp:
  // - device gear bonus when the event's primary technique is tooled up
  // - the equipped skill artifact when it matches the event's skill
  // - the Crosslink Amplifier when a synergy variant actually fired
  const gearBonus = availableTechniques.includes(event.relatedTechniqueId)
    ? (getEffect('technique-success-bonus')?.value || 0) / 100
    : 0;
  const artifactBonus =
    getSkillArtifactBonus(event.relatedSkillId || event.favoredSkill, state.character) / 100 +
    (variant.variantIndex > 0 ? getUtilityArtifactBonus('faction-synergy-bonus', state.character) / 100 : 0) +
    // Training Node Deck: only when this room actually has support skills
    (room.support?.length > 0 ? getUtilityArtifactBonus('support-skill-bonus', state.character) / 100 : 0);
  const outcome = resolveSkillCheckOutcome(event, variant, teamSkills, tier, room, gearBonus + artifactBonus);

  // Who the map should play this on. The check resolves against the team's
  // BEST level in the event's skill, so the member holding that level is the
  // one who ran it — which is the honest answer and also the one the player
  // would name if you asked them.
  const actor = leadOn(event.relatedSkillId || event.favoredSkill, team);

  // Step 5a: If success - generate loot based on rarity drops (like bonus loot)
  let eventLoot = [];
  // Whoever the failure actually landed on, for the map. Declared out here
  // because the damage result is scoped to the failure branch.
  let hitMember = null;
  if (outcome.success) {
    // Generate skill-check event loot using rarity-based drops (common, uncommon, and rare).
    // The pool is filtered to the rolled rarity - the skill's loot pool bottoms out in
    // commons, and an unfiltered pick made every rarity roll shovel mostly commons.
    const eventRarities = ['common', 'uncommon', 'rare'];
    const fullLootPool = generateSkillEventLootPool(event, state);
    for (const rarity of eventRarities) {
      const dropChance = getRarityDropChance(rarity, tier, room.type);
      if (Math.random() < dropChance) {
        const lootPool = fullLootPool.filter(itemId => findItem('id', itemId)?.rarity === rarity);
        if (lootPool.length > 0) {
          // Get quantity range for this rarity (utility artifacts can boost)
          const quantityRange = getRarityQuantityRange(rarity, tier, room.type);
          const quantity = applyLootQuantityBonus(typeof quantityRange === 'number'
            ? quantityRange
            : Math.round(Math.random() * (quantityRange.max - quantityRange.min)) + quantityRange.min,
            room, state.character);

          // Select items from skill-specific loot pool, weighted by per-item drop weights
          for (let q = 0; q < quantity && lootPool.length > 0; q++) {
            const picked = pickWeightedItem(lootPool);
            if (picked) eventLoot.push(picked);
          }
        }
      }
    }

    // Synergy bonus: a synergy variant's combined approach recovers one
    // extra guaranteed salvage pick - rare/uncommon at their normal odds,
    // falling back to a common so the jackpot never comes up empty
    if (variant.variantIndex > 0) {
      for (const rarity of ['rare', 'uncommon', 'common']) {
        const pool = fullLootPool.filter(itemId => findItem('id', itemId)?.rarity === rarity);
        if (pool.length === 0) continue;
        if (rarity !== 'common' && Math.random() >= getRarityDropChance(rarity, tier, room.type)) continue;
        const picked = pickWeightedItem(pool);
        if (picked) { eventLoot.push(picked); break; }
      }
    }
  }
  // Step 5b: If fail - apply damage. The Hazard Lens Visor softens hazard
  // room damage; Recovery Injector / Auto-Stabilizer Pack catch the FIRST
  // team member who would fall this raid (once per raid).
  else {
    const raid = state.currentRaid.raidInProgress;
    const hazardReduction = room.type === 'hazard'
      ? getUtilityArtifactBonus('hazard-damage-reduction', state.character) : 0;
    const canPreventKnockout =
      getUtilityArtifactBonus('knockout-prevention', state.character) > 0 && !raid.knockoutPrevented;
    const damageResult = applySkillEventDamage(tier, team, {
      damageMultiplier: 1 - hazardReduction / 100,
      preventKnockout: canPreventKnockout,
    });
    debug(CH.events, "DAMAGED", damageResult.damage);
    eventData.damage = damageResult.damage;
    eventData.casualties = damageResult.casualties;
    hitMember = damageResult.member;
    raid.hasSkillEventFailure = true;
    if (damageResult.knockoutPrevented) {
      raid.knockoutPrevented = true;
      addRaidMessage(`🛡️ ${damageResult.member.name} is stabilized at the brink — the injector fires before they fall!`, 'bonus', {
        stepIndex: currentRoomIndex,
        notificationMessage: `🛡️ ${damageResult.member.name} stabilized`
      });
    }
  }

  // The map's version of the same news. Posted here rather than off the log
  // messages below, because a log line is prose and this has to key off what
  // actually happened: a synergy variant is `variantIndex > 0`, and a failure
  // is played on whoever took the hit, not on whoever ran the check.
  if (outcome.success) {
    emitRaidFx(variant.variantIndex > 0 ? 'synergy' : 'success', { memberId: actor?.id });
  } else {
    emitRaidFx('failure', { memberId: (hitMember ?? actor)?.id });
  }

  // Step 6: Show UI message with technique name substitutions
  const messageText = interpolateTechNames(
    outcome.success ? (variant.success || 'Event succeeded!') : (variant.fail || 'Event failed!'),
    event, variant);

  // Marker and title OUTSIDE the flavour span, matching logEventProblem's
  // shape: the header is what happened, the prose is what it felt like. They
  // shared one span until the prose was styled down, which would have taken
  // the tick or the cross down with it.
  addRaidMessage(`${outcome.success ? '✓' : '✗'} ${variant.title}<div class="event-outcome-text">${messageText}</div>`,
    outcome.success ? 'success' : 'failure',
    {
      stepIndex: currentRoomIndex,
      notificationMessage: `${outcome.success ? '✓' : '✗'} ${variant.title}`
    }
  );

  // Step 6a: On success, celebrate a fired synergy variant and auto-deploy
  // a technique consumable (if carried)
  if (outcome.success) {
    if (variant.variantIndex > 0 && variant.synergyTechniqueId) {
      const synergyTechniqueName = getTechniqueName(variant.synergyTechniqueId);
      addRaidMessage(`⚡ Synergy: ${synergyTechniqueName} amplifies the approach — extra salvage recovered!`, 'bonus', {
        stepIndex: currentRoomIndex,
        notificationMessage: `⚡ Synergy: ${synergyTechniqueName}`
      });
    }
    deployTechniqueConsumable(event, state, room);
  }

  // Step 6b: Award loot and credits on success
  if (outcome.success && eventLoot.length > 0) {
    // Group loot by item ID to show quantities
    const lootMap = {};
    eventLoot.forEach(item => {
      lootMap[item] = (lootMap[item] || 0) + 1;
    });

    // Award loot and display messages
    let lootMessage = '';
    Object.entries(lootMap).forEach(([itemId, amount], idx) => {
      addLoot(state.currentRaid.raidInProgress.lootGained, itemId, amount);
      const itemName = getItemName(itemId);
      lootMessage += (idx == 0 ? '' : ', ') + `${amount}x ${itemName}`;
      // One mote per distinct find, held back a beat each so a good haul
      // arrives as a run of finds rather than a single flash. The rarity is
      // what the mote is coloured by, which is the whole point of showing it
      // on the map at all: a rare coming up looks different from a common.
      emitRaidFx('loot', {
        memberId: actor?.id,
        rarity: findItem('id', itemId)?.rarity,
        delayMs: 260 + idx * 220,
      });
    });
    addRaidMessage(`⚡ Salvaged: ${lootMessage}!`, 'bonus', {
      stepIndex: currentRoomIndex
    });

    // Award event success credits
    const tierData = config.raidTierData[tier];
    if (tierData && tierData.creditRewards) {
      let eventCredits = tierData.creditRewards.successEventCredit;
      // Apply credit scaling multipliers
      eventCredits = applyCreditsScaling(eventCredits, tier);
      addLoot(state.currentRaid.raidInProgress.lootGained, 'Credits', eventCredits);
      addRaidMessage(`💰 Intelligence Bonus: ${eventCredits} Credits`, 'bonus', {
        stepIndex: currentRoomIndex
      });
    }
  }

  // Step 7: Mark event as completed
  eventData.status = outcome.success ? 'completed' : 'failed';

  return {
    success: outcome.success,
    variant: variant,
    loot: eventLoot,
    damage: eventData.damage || 0,
    casualties: eventData.casualties || [],
    outcome
  };
}

/**
 * Boost a material quantity by the equipped utility artifact's yield
 * effects: Salvage Compression Rig (+X% everywhere) and Material Extractor
 * (+X% in gate rooms). Small integers, so the fraction rounds
 * probabilistically - a +10% on qty 2 is a 20% chance of one extra.
 */
export function applyLootQuantityBonus(quantity, room, character) {
  let pct = getUtilityArtifactBonus('loot-quantity-bonus', character);
  if (room?.type === 'gate') pct += getUtilityArtifactBonus('gate-material-bonus', character);
  if (pct <= 0 || quantity <= 0) return quantity;
  const boosted = quantity * (1 + pct / 100);
  const floor = Math.floor(boosted);
  return floor + (Math.random() < boosted - floor ? 1 : 0);
}

/**
 * Rig a technique consumable after a successful skill-check (per-raid).
 *
 * The FIRST success of a technique consumes one matching consumable set from
 * the active character and records it in raid.riggedTechniques - the rig then
 * stays installed for the rest of the raid, so every later success of that
 * technique gets the time cut for free. The cut jumps the room's clock
 * forward by the item's room-time-reduction percentage of baseDuration: both
 * roomStartTime and nextRoomTime shift together so the whole room timeline
 * compresses uniformly - later scheduled events (including the end-of-room
 * bonus loot window) fire sooner instead of being skipped. No-op when the
 * room has no real duration (instant simulation): nothing is consumed.
 *
 * @returns {Object|null} { item, reductionSec, firstRig } when a rig applied
 */
export function deployTechniqueConsumable(event, state, room) {
  const raid = state.currentRaid?.raidInProgress;
  if (!raid) return null;
  if (!room?.baseDuration || room.baseDuration <= 0) return null;

  const techniqueId = event.relatedTechniqueId;
  if (!techniqueId) return null;

  const rigged = raid.riggedTechniques = raid.riggedTechniques || {};
  let item = null;
  let firstRig = false;

  if (rigged[techniqueId]) {
    item = findItem('id', rigged[techniqueId]);
  } else {
    const inventory = state.character?.inventory;
    if (!inventory) return null;
    const itemId = Object.keys(inventory).find(id => {
      if ((inventory[id] || 0) <= 0) return false;
      const candidate = findItem('id', id);
      return candidate?.type === 'consumable' && candidate.techniqueId === techniqueId &&
        (candidate.effects?.['room-time-reduction'] || 0) > 0;
    });
    if (!itemId) return null;
    item = findItem('id', itemId);
    inventory[itemId]--;
    if (inventory[itemId] <= 0) delete inventory[itemId];
    rigged[techniqueId] = itemId;
    firstRig = true;
  }
  if (!item) return null;

  const pct = item.effects?.['room-time-reduction'] || 0;
  if (pct <= 0) return null;
  const reductionSec = room.baseDuration * (pct / 100);
  const reductionMs = reductionSec * 1000;

  raid.roomStartTime -= reductionMs;
  raid.nextRoomTime -= reductionMs;

  addRaidMessage(firstRig
    ? `${item.icon} ${item.name} rigged for this raid — ${getTechniqueName(techniqueId)} rooms run ${pct}% faster (−${Math.round(reductionSec)}s)`
    : `${item.icon} ${item.name}: cleared ${Math.round(reductionSec)}s off the room`, 'bonus', {
    stepIndex: raid.currentRoomIndex,
    notificationMessage: firstRig
      ? `${item.icon} ${item.name} rigged for this raid`
      : `${item.icon} −${Math.round(reductionSec)}s off the room`
  });
  return { item, reductionSec, firstRig };
}

// ---------------------------------------------------------------------------
// Synergy partner maps (cached from event definitions). Every event carries
// two cross-faction synergy variants; these lookups answer "which techniques
// can upgrade this skill's events" and the reverse - the data behind the
// contractor-hiring synergy indicators.
// ---------------------------------------------------------------------------

let synergyPartnersBySkill = null;      // skillId -> Set<partner techniqueId>
let synergySkillsByTechnique = null;    // techniqueId -> Set<skillId it upgrades>
let synergyPartnersByTechnique = null;  // primary techniqueId -> Set<partner techniqueId>

function buildSynergyMaps() {
  synergyPartnersBySkill = {};
  synergySkillsByTechnique = {};
  synergyPartnersByTechnique = {};
  const allEvents = [
    ...config.eventDefinitionsDominion,
    ...config.eventDefinitionsWilders,
    ...config.eventDefinitionsVeil
  ];
  for (const event of allEvents) {
    const skill = event.relatedSkillId || event.favoredSkill;
    for (const v of event.variants || []) {
      if (!v.synergyTechniqueId) continue;
      (synergyPartnersBySkill[skill] ||= new Set()).add(v.synergyTechniqueId);
      (synergySkillsByTechnique[v.synergyTechniqueId] ||= new Set()).add(skill);
      if (event.relatedTechniqueId) {
        (synergyPartnersByTechnique[event.relatedTechniqueId] ||= new Set()).add(v.synergyTechniqueId);
      }
    }
  }
}

/** Techniques whose gear can upgrade the given skill's events to synergy variants. */
export function getSkillSynergyPartners(skillId) {
  if (!synergyPartnersBySkill) buildSynergyMaps();
  return synergyPartnersBySkill[skillId] || new Set();
}

/** Skills whose events the given technique's gear can upgrade. */
export function getSynergySkillsForTechnique(techniqueId) {
  if (!synergySkillsByTechnique) buildSynergyMaps();
  return synergySkillsByTechnique[techniqueId] || new Set();
}

/** Partner techniques for a specific primary technique's events. */
export function getSynergyPartnersForTechnique(techniqueId) {
  if (!synergyPartnersByTechnique) buildSynergyMaps();
  return synergyPartnersByTechnique[techniqueId] || new Set();
}

/**
 * Select the best available variant for an event based on team techniques
 * Priority: synergy variant 1 > synergy variant 2 > primary-only
 * @param {Object} event - Event definition
 * @param {Array} availableTechniques - Array of technique IDs team has
 * @returns {Object} Selected variant with full context
 */
export function selectEventVariant(event, availableTechniques) {
  const variants = event.variants || [];
  if (variants.length === 0) {
    console.warn(`Event ${event.id} has no variants`);
    return null;
  }

  const techSet = new Set(availableTechniques);

  // Priority: variant[1] synergy > variant[2] synergy > variant[0] primary
  for (let i = 1; i < Math.min(3, variants.length); i++) {
    if (variants[i].synergyTechniqueId && techSet.has(variants[i].synergyTechniqueId)) {
      return { ...variants[i], variantIndex: i };
    }
  }

  // Fallback to primary-only variant
  return { ...variants[0], variantIndex: 0 };
}

/**
 * Which variant of an event this team will get, and the technique pool that
 * decided it. Selection is DETERMINISTIC and depends only on raid-stable state
 * (the hired team's equipped gear plus the player's synergy unlocks), so the
 * same answer holds from the moment the event starts to the moment it resolves
 * — which is what lets logEventProblem announce the scenario that will actually
 * play out. Callers that have no team yet get the primary variant, matching the
 * old behaviour.
 * @param {Object} event - Event definition
 * @param {Object} state - gameState object
 * @param {Array} team - Current raid team
 * @returns {Object} { variant, availableTechniques }
 */
/**
 * Whose check this is: the member carrying the team's best level in the skill.
 *
 * The outcome is resolved against `max(level)` across the team, so this is not
 * a guess at who ran it — it is the member whose level the roll actually used.
 * Ties go to the first, which puts the player ahead of their contractors.
 *
 * Only the map cares. Nothing about the outcome depends on it.
 */
export function leadOn(skillId, team) {
  if (!skillId || !team?.length) return team?.[0] || null;
  let best = null, bestLevel = -1;
  for (const m of team) {
    if (m?.isAlive === false) continue;
    const level = m?.skills?.[skillId] || 0;
    if (level > bestLevel) { bestLevel = level; best = m; }
  }
  return best || team.find(m => m?.isAlive !== false) || team[0];
}

export function resolveEventVariant(event, state, team) {
  // Available techniques = the team's equipped skill gear (one per skill per
  // member). Gear is the gate: skills make a technique attemptable, the
  // equipped tool unlocks synergy variants and the success bonus. Members may
  // be raw character/contractor objects (equippedTechniqueGear map) or
  // initializeRaidTeam snapshots (equippedTechniques array).
  const availableTechniques = [];
  (team || []).forEach(member => {
    const techniques = member.equippedTechniques || Object.keys(member.equippedTechniqueGear || {});
    techniques.forEach(t => availableTechniques.push(t));
  });

  // Per-skill synergy unlocks gate WHICH techniques may drive a synergy variant
  // (either-side rule): allowed when the event's skill is unlocked, or when the
  // technique is the player's own gear from an unlocked skill.
  const character = state?.character;
  const playerTechniques = new Set(Object.keys(character?.equippedTechniqueGear || {}));
  const eventSkillUnlocked = isSynergyUnlocked(event.relatedSkillId || event.favoredSkill, character);
  const synergyCapable = availableTechniques.filter(t =>
    eventSkillUnlocked || (playerTechniques.has(t) && isSynergyUnlocked(getTechniqueSkill(t), character)));

  return { variant: selectEventVariant(event, synergyCapable), availableTechniques };
}

/**
 * Substitute @techName / @synergyTechName in event prose. Shared by the problem
 * statement and the outcome text so a placeholder can never render raw in one
 * place while working in the other.
 */
export function interpolateTechNames(text, event, variant) {
  if (!text) return text;
  let out = text.replace(/@techName/g, getTechniqueName(event.relatedTechniqueId));
  if (variant?.synergyTechniqueId) {
    out = out.replace(/@synergyTechName/g, getTechniqueName(variant.synergyTechniqueId));
  }
  return out;
}

/**
 * Resolve skill-check event outcome using raid success formula
 * @param {Object} event - Event definition with favoredSkill, favoredTechnique
 * @param {Object} variant - Selected variant
 * @param {Object} teamSkills - Team skill levels { skillId: level, ... }
 * @param {Number} raidTier - Current raid tier (1-3)
 * @param {Array} room - Current room object with support skills
 * @returns {Object} { success: boolean, chance: number, roll: number }
 */
/**
 * The skill-check success formula, exposed for UI surfacing (skill cards,
 * raid planning): 76% baseline, +/-5% per level vs the room target, support
 * bonus on top, clamped to [20%, 97%].
 */
export function getSkillCheckChance(skillLevel, targetSkillLevel, supportBonus = 0) {
  const chance = 0.76 + ((skillLevel - targetSkillLevel) * 0.05) + supportBonus;
  return Math.max(0.20, Math.min(0.97, chance));
}

export function resolveSkillCheckOutcome(event, variant, teamSkills, raidTier, room, gearBonus = 0) {
  // Get target skill level from raid tier configuration
  const tierData = config.raidTierData[raidTier] || { gateSkillLevel: 50, hazardSkillLevel: 40 };
  const targetSkillLevel = room.type === 'gate' ? tierData.gateSkillLevel : tierData.hazardSkillLevel;

  // Get primary skill level for this event
  const primarySkill = event.favoredSkill || event.relatedSkillId;
  const primarySkillLevel = teamSkills[primarySkill] || 0;

  // Calculate skill difference
  const skillDiff = primarySkillLevel - targetSkillLevel;

  // Calculate support bonus from support skills (capped at 0.15)
  let supportBonus = 0;
  if (room.support && Array.isArray(room.support)) {
    const supportSum = room.support.reduce((sum, skillId) => sum + (teamSkills[skillId] || 0), 0);
    supportBonus = Math.min(supportSum * 0.1, 0.15);
  }

  // Calculate success chance using raid formula. The equipped-gear bonus
  // rides through the same clamp, so the 97% ceiling still applies.
  const successChance = getSkillCheckChance(primarySkillLevel, targetSkillLevel, supportBonus + gearBonus);

  // Roll 1d100 (0-100)
  const roll = Math.random() * 100;
  const success = roll < successChance * 100;

  return {
    success,
    chance: successChance,
    roll: roll / 100,
    skillDiff,
    supportBonus,
    gearBonus,
    targetSkillLevel,
    primarySkillLevel
  };
}

/**
 * Generate loot pool for skill-check event from crafting recipes
 * Recursively builds pool from materials needed to craft skill-related items
 * @param {Object} event - Event definition with favoredSkill and favoredTechnique
 * @param {Object} state - gameState object
 * @returns {Array} Array of material/consumable item IDs that can drop
 */
export function generateSkillEventLootPool(event, state) {
  const favoredSkill = event.favoredSkill || event.relatedSkillId;
  const favoredTechnique = event.favoredTechnique || event.relatedTechniqueId;
  const materialIds = new Set();
  const visitedItems = new Set();

  // Get all recipes
  const allRecipes = [
    ...(config.recipesUncommon || []),
    ...(config.recipesRare || []),
    ...(config.recipesEpic || []),
    ...(config.recipesArtifact || [])
  ];

  // Get all craftable items (excludes common and blueprints which can't be crafted)
  const craftableItems = [
    ...(config.itemsUncommon || []),
    ...(config.itemsRare || []),
    ...(config.itemsUncommonConsumables || []),
    ...(config.itemsRareConsumables || []),
    ...(config.itemsEpicConsumableDominion || []),
    ...(config.itemsEpicConsumableWilders || []),
    ...(config.itemsEpicConsumableVeil || []),
    ...(config.itemsEpicDeviceDominion || []),
    ...(config.itemsEpicDeviceWilders || []),
    ...(config.itemsEpicDeviceVeil || []),
    ...(config.itemsArtifactsSkill || []),
    ...(config.itemsArtifactsUtility || [])
  ];

  /**
   * Recursively collect every recipe component at every depth: craftable
   * intermediates (uncommon/rare materials, consumables) AND base leaves.
   * The room's skill loot must span the whole chain toward its epic
   * gear/artifacts, not just the commons the chain bottoms out in —
   * otherwise uncommon/rare rarity rolls find an empty pool and drop nothing.
   */
  const collectIngredients = (itemId) => {
    if (visitedItems.has(itemId)) return;
    visitedItems.add(itemId);

    const recipe = allRecipes.find(r => r.target === itemId);
    if (!recipe || !recipe.inputs) return;

    recipe.inputs.forEach(input => {
      if (!input.id) return;
      materialIds.add(input.id);
      collectIngredients(input.id);
    });
  };

  // Find all items related to this skill/technique and collect ingredients
  craftableItems
    .filter(item => {
      // Match by skill (for skill artifacts) or by techniqueId (for epic items)
      return item.skill === favoredSkill || item.techniqueId === favoredTechnique;
    })
    .forEach(item => collectIngredients(item.id));

  debug(CH.loot, `Loot pool for event ${event.id} (skill: ${favoredSkill}, technique: ${favoredTechnique}): ${materialIds.size} materials`);

  return Array.from(materialIds);
}

/**
 * Apply damage to random team member on event failure
 * Uses tier-based damage scaling like the main raid system
 * @param {Number} raidTier - Current raid tier (1-3, used for damage scaling)
 * @param {Array} team - Team roster with members
 * @param {Object} options - { damageMultiplier: scales the rolled damage
 *   (Hazard Lens Visor passes <1 for hazard rooms); preventKnockout: when
 *   true, a member who would fall is stabilized at 1 HP instead (the call
 *   site owns the once-per-raid bookkeeping) }
 * @returns {Object} { damaged, member, damage, casualties, knockoutPrevented }
 */
export function applySkillEventDamage(raidTier, team, options = {}) {
  const { damageMultiplier = 1, preventKnockout = false } = options;
  if (!team || team.length === 0) {
    debug(CH.raid, "TEAM MISSING");
    return { damaged: false, member: null, damage: 0, casualties: [] };
  }

  // Find alive team members only
  const aliveMembers = team.filter(m => m.isAlive);
  if (aliveMembers.length === 0) {
    debug(CH.raid, "TEAM DEAD ALREADY");
    return { damaged: false, member: null, damage: 0, casualties: [] };
  }

  // Select random alive member
  const member = aliveMembers[Math.floor(Math.random() * aliveMembers.length)];

  // Apply damage using configurable tier scaling
  const dmgConfig = config.progressionScaling?.skillEventDamage || {};
  let minDamage, maxDamage;

  // Check if per-tier damage is configured
  if (dmgConfig.perTier && dmgConfig.perTier[raidTier]) {
    const tierDamage = dmgConfig.perTier[raidTier];
    minDamage = tierDamage.min ?? 5;
    maxDamage = tierDamage.max ?? 15;
  } else {
    // Fall back to baseDamage * tier formula
    const baseDamage = dmgConfig.baseDamage ?? 5;
    minDamage = baseDamage * raidTier;
    maxDamage = minDamage + (baseDamage * raidTier);
  }

  const damage = Math.max(1, Math.round(
    (minDamage + Math.floor(Math.random() * (maxDamage - minDamage + 1))) * damageMultiplier));

  member.currentHp = Math.max(0, member.currentHp - damage);

  const casualties = [];
  let knockoutPrevented = false;
  if (member.currentHp <= 1) {
    if (preventKnockout) {
      // Stabilized at the brink instead of falling
      member.currentHp = 1;
      knockoutPrevented = true;
    } else {
      member.isAlive = false;
      casualties.push(member);
    }
  }

  return { damaged: true, member, damage, casualties, knockoutPrevented };
}

// ============================================================================
// Bonus Loot Event
// ============================================================================

/**
 * Check if all skill-check events in room succeeded (none failed)
 * @param {Object} state - gameState object
 * @returns {boolean}
 */
export function shouldTriggerBonusLoot(state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return false;
  }
  // Bonus triggers only if no skill event failures occurred
  return !state.currentRaid.raidInProgress.hasSkillEventFailure;
}

/**
 * Schedule bonus loot event at room end, conditional on skill event success
 * Bonus event scheduled 15 seconds before room completion to trigger during final segment
 * @param {Object} room - Room object
 * @param {Object} state - gameState object
 */
export function scheduleConditionalBonusLoot(room, state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return;
  }

  // Schedule bonus at room duration - 15 seconds (converted to milliseconds)
  const bonusEventTimeMs = room.baseDuration == 0 ? 0 : (room.baseDuration - 15) * 1000;

  state.currentRaid.raidInProgress.events.bonusLootEvent = {
    scheduled: true,
    scheduledTimeMs: bonusEventTimeMs,
    triggered: false,
    active: false,
    element: null,
    roomIndex: state.currentRaid.raidInProgress.currentRoomIndex
  };

  debug(CH.events, `Scheduled bonus loot event at ${bonusEventTimeMs / 1000}s for room ${room.type}`);
}

/**
 * Generate bonus loot at specific rarity tier
 * @param {Object} room - Room object
 * @param {Number} tier - Raid tier
 * @param {string} rarity - Rarity tier
 * @returns {Object} Loot object { itemId: quantity, ... }
 */
export function generateBonusLoot(room, tier, rarity, character = null) {
  const loot = {};
  const quantityRange = getRarityQuantityRange(rarity, tier, room.type);
  const quantity = applyLootQuantityBonus(
    Math.round(Math.random() * (quantityRange.max - quantityRange.min)) + quantityRange.min,
    room, character);
  const itemPool = getItemPoolForRarity(room, tier, rarity);

  if (rarity === 'legendary') {
    debug(CH.loot, `[LEGENDARY] generateBonusLoot: tier=${tier}, itemPool.length=${itemPool.length}, quantity=${quantity}`);
  }

  // Relic Resonance Tuner: relics get a heavier hand inside mixed pools
  // without inflating the rest of the pool's drop odds
  const relicBoostPct = getUtilityArtifactBonus('relic-quality-bonus', character);
  const boost = relicBoostPct > 0
    ? { predicate: id => findItem('id', id)?.type === 'relic', multiplier: 1 + relicBoostPct / 100 }
    : null;

  // Select item from pool, weighted by per-item drop weights
  if (itemPool.length > 0) {
    const item = pickWeightedItem(itemPool, boost);
    loot[item] = quantity;
    if (rarity === 'legendary') {
      debug(CH.loot, `[LEGENDARY] Selected item: ${item}`);
    }
  } else if (rarity === 'legendary') {
    console.warn(`[LEGENDARY] No items in pool! Cannot generate loot.`);
  }

  return loot;
}

/**
 * Get item pool for a specific rarity tier
 * @param {Object} room - Room object
 * @param {Number} tier - Raid tier
 * @param {string} rarity - Rarity tier
 * @returns {Array} Array of item IDs that can drop at this rarity
 */
// Union of the room's skill-chain pools (every recipe component reachable
// from the epic gear/artifacts tied to the room's primary+support skills).
// Cached per skill signature — pure config data.
const roomChainCache = new Map();
function getRoomChainMaterials(room) {
  const key = `${room.primary || ''}|${(room.support || []).slice().sort().join(',')}`;
  if (roomChainCache.has(key)) return roomChainCache.get(key);
  const set = new Set();
  for (const event of getEventsForRoom(room, null)) {
    for (const id of generateSkillEventLootPool(event, null)) set.add(id);
  }
  roomChainCache.set(key, set);
  return set;
}

export function getItemPoolForRarity(room, tier, rarity) {
  let itemPool = [];
  switch (rarity) {
    case 'common':
    case 'uncommon':
    case 'rare': {
      // Scope bonus materials to the room's skill chains so bonus events
      // reinforce the room's signature instead of re-washing every faction
      // material into every faction room. Fall back to the faction pool if
      // the intersection is empty (e.g. a room with no chain materials at
      // this rarity) so the bonus roll never comes up empty-handed.
      const factionPool = getFactionSpecificMaterials(room, rarity);
      const chain = getRoomChainMaterials(room);
      const scoped = factionPool.filter(id => chain.has(id));
      itemPool = scoped.length > 0 ? scoped : factionPool;
      break;
    }
    case 'epic':
      const tierRarity = regionTierToRarity(tier);
      // Exclude HP/Energy consumables from epic loot (now purchaseable only)
      const epicConsumables = getItemsByType('consumable', tierRarity)
        .filter(item => item !== 'field-health-stim' && item !== 'portable-energy-pack' &&
                        item !== 'recovery-injector-mk2' && item !== 'energy-conversion-pack' &&
                        item !== 'advanced-recovery-serum' && item !== 'power-cell-mk3');
      itemPool = epicConsumables.concat(getItemsByType('relic', tierRarity));
      break;
    case 'legendary':
      itemPool = getBlueprintsForRoom(room);
      debug(CH.loot, `[LEGENDARY] getItemPoolForRarity: found ${itemPool.length} blueprints for faction ${getRoomFaction(room)}`);
      if (itemPool.length === 0) {
        console.warn(`[LEGENDARY] WARNING: No blueprints found for room with primary=${room.primary}`);
      }
      break;
    case 'unique':
      itemPool = tier >= 4 ? getItemsByType('storyRelic') : [];
      break;
    default:
      itemPool = [];
  }
  return itemPool;
}

// ============================================================================
// PLANNING: which items a room could drop (for the raid-grid item highlight)
// ============================================================================

const SKILL_CHECK_RARITIES = ['common', 'uncommon', 'rare'];
const ALL_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];

/**
 * Compute the set of item IDs a room could drop at a given tier — an exact
 * mirror of the runtime drop sources, so the planning highlight and the
 * actual loot engine can never disagree:
 *   - skill-check loot: every component (intermediates + leaves) of the
 *     recipes for epic gear/artifacts tied to the room's skills
 *     (generateSkillEventLootPool over the room's events), at any droppable
 *     common/uncommon/rare rarity;
 *   - bonus loot: getItemPoolForRarity per droppable rarity (faction
 *     materials, consumables/relics, skill-tied blueprints, story relics);
 *   - water rooms drop only 'Fish'. Credits are never included.
 * Deterministic given (room, tier). opts.itemsById is an optional
 * Map<id,item> so callers batching many rooms skip rebuilding the catalog.
 * @param {Object} room - materialized grid room
 * @param {number} tier - raid tier
 * @param {Object} [opts] - { itemsById?: Map }
 * @returns {Set<string>} droppable item IDs
 */
export function getRoomDroppableItemIds(room, tier, opts = {}) {
  const ids = new Set();
  if (!room || !room.type) return ids;
  if (room.type === 'water') { ids.add('Fish'); return ids; }
  if (room.type !== 'gate' && room.type !== 'hazard') return ids;

  const itemsById = opts.itemsById || new Map(getAllItems().map(i => [i.id, i]));
  const canDrop = {};
  for (const r of ALL_RARITIES) canDrop[r] = getRarityDropChance(r, tier, room.type) > 0;

  // Skill-check pool — recipe components tied to the room's skills
  for (const event of getEventsForRoom(room, null)) {
    for (const itemId of generateSkillEventLootPool(event, null)) {
      const rarity = itemsById.get(itemId)?.rarity;
      if (SKILL_CHECK_RARITIES.includes(rarity) && canDrop[rarity]) ids.add(itemId);
    }
  }

  // Bonus-loot pools per droppable rarity
  for (const r of ALL_RARITIES) {
    if (!canDrop[r]) continue;
    for (const itemId of getItemPoolForRarity(room, tier, r)) ids.add(itemId);
  }

  ids.delete('Credits');
  return ids;
}

/**
 * Build a per-grid loot index for the item-highlight picker. Rooms sharing a
 * type+skills signature compute their droppable set once (a T3 grid's ~40
 * gate/hazard rooms collapse to a handful of distinct sets), so the whole
 * build stays well under ~100ms.
 * @param {Object} gridData - currentRaidGrid { grid, rows, cols }
 * @returns {{ itemToCells: Map<string,{row:number,col:number}[]>, items: {id,rarity,count}[] }}
 */
export function buildGridLootIndex(gridData) {
  const itemToCells = new Map();
  if (!gridData || !gridData.grid) return { itemToCells, items: [] };

  const itemsById = new Map(getAllItems().map(i => [i.id, i]));
  const tier = gridData.tier;
  const rows = gridData.rows || gridData.grid.length;
  const cols = gridData.cols || (gridData.grid[0] ? gridData.grid[0].length : 0);
  const bySignature = new Map(); // signature -> Set<itemId>

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const room = gridData.grid[row]?.[col];
      if (!room || room.type === 'blocked') continue;
      const sig = `${room.type}|${room.primary || ''}|${(room.support || []).slice().sort().join(',')}`;
      let set = bySignature.get(sig);
      if (!set) { set = getRoomDroppableItemIds(room, tier, { itemsById }); bySignature.set(sig, set); }
      for (const id of set) {
        let cells = itemToCells.get(id);
        if (!cells) { cells = []; itemToCells.set(id, cells); }
        cells.push({ row, col });
      }
    }
  }

  // Sorted distinct item list: by rarity order, then display name. 'Fish' has
  // no catalog entry — bucket it last as a "Special" group.
  const rarityOrder = r => config.itemRarities?.[r]?.order ?? 99;
  const items = Array.from(itemToCells.keys()).map(id => {
    const item = itemsById.get(id);
    return { id, type: item?.type || '', rarity: item?.rarity || 'special', count: itemToCells.get(id).length };
  }).sort((a, b) => {
    const oa = a.rarity === 'special' ? 100 : rarityOrder(a.rarity);
    const ob = b.rarity === 'special' ? 100 : rarityOrder(b.rarity);
    if (oa !== ob) return oa - ob;
    return getItemName(a.id).localeCompare(getItemName(b.id));
  });

  return { itemToCells, items };
}

/**
 * Cleanup bonus loot event when done or room ends
 * @param {Object} state - gameState object
 */
export function cleanupBonusLootEvent(state) {
  const bonusEvent = state.currentRaid.raidInProgress.events.bonusLootEvent;

  if (typeof document != 'undefined' && bonusEvent.element && bonusEvent.element.parentNode) {
    bonusEvent.element.remove();
  }

  bonusEvent.active = false;
  bonusEvent.scheduled = false;
  bonusEvent.triggered = false;
  bonusEvent.element = null;
  bonusEvent.roomIndex = null;
}

export function spawnBonusLootEventCore(state) {
  const bonusEvent = state.currentRaid.raidInProgress.events.bonusLootEvent;
  const currentRoomIndex = state.currentRaid.raidInProgress.currentRoomIndex;

  // Verify we're still in the right room
  if (currentRoomIndex !== bonusEvent.roomIndex) {
    bonusEvent.scheduled = false;
    return;
  }

  // Distribute bonus loot (starting with common rarity, cascades up)
  processBonusLootEvent(state, 'common');

  // Clean up the event
  cleanupBonusLootEvent(state);
}

/**
 * Generate and award bonus loot with rarity cascading
 * @param {Object} state - gameState object
 * @param {string} [rarity='common'] - Starting rarity tier
 */
export function processBonusLootEvent(state, rarity = 'common') {
  if (state.currentRaid.raidInProgress == null) {
    debug(CH.events, "tried to process bonus loot event after raid ended");
    return;
  }

  const currentRoomIndex = state.currentRaid.raidInProgress.currentRoomIndex;
  const pathStep = state.currentRaid.selectedPath[currentRoomIndex];
  const room = pathStep.room;
  const tier = state.currentRaid.currentRaidGrid.tier;

  if (rarity === 'common') {
    // The base bonus rewards faction intel CREDITS instead of common items:
    // the team logs field data valuable to the faction. Keeps common material
    // income demand-shaped (weighted event loot only) and feeds the shared
    // credit pool that multiplayer team members split.
    const tierData = config.raidTierData[tier];
    const intelCredits = applyCreditsScaling(tierData?.creditRewards?.successEventCredit || 15, tier);
    addLoot(state.currentRaid.raidInProgress.lootGained, 'Credits', intelCredits);
    addRaidMessage(`🧠 Field Intelligence: the team files what this site remembers — ${intelCredits} credits!`, 'bonus', {
      stepIndex: currentRoomIndex,
      notificationMessage: `🧠 Field Intelligence: +${intelCredits} credits`
    });
  } else {
    // Cascaded tiers award items from the room-faction pool
    const bonusLoot = generateBonusLoot(room, tier, rarity, state.character);

    Object.entries(bonusLoot).forEach(([item, amount]) => {
      addLoot(state.currentRaid.raidInProgress.lootGained, item, amount);

      const itemName = getItemName(item);
      const rarityIcons = { rare: '💎', epic: '📜', legendary: '📜', unique: '🌟' };
      const icon = rarityIcons[rarity] || '✨';
      addRaidMessage(`${icon} Exploration Bonus: Found ${amount}x ${itemName}!`, 'bonus', {
        stepIndex: currentRoomIndex,
        notificationMessage: `${icon} Exploration Bonus: ${amount}x ${itemName}!`
      });
    });

    debug(CH.loot, `Bonus loot (${rarity}) awarded:`, bonusLoot);
  }

  // Cascade to next rarity tier using same drop chances as normal loot
  const rarityProgression = { common: 'rare', rare: 'epic', epic: 'legendary', legendary: 'unique' };
  const nextRarity = rarityProgression[rarity];

  if (nextRarity) {
    let cascadeChance = getRarityDropChance(nextRarity, tier, room.type);

    // Apply per-rarity cascade multiplier from config
    const cascadeMultiplier = getCascadeMultiplier(tier, nextRarity);
    cascadeChance = cascadeChance * cascadeMultiplier;

    // Memory Crystal Shard: relative boost on the step INTO legendary
    // (where blueprints live) - "the crystal resonates with blueprint caches"
    if (nextRarity === 'legendary') {
      cascadeChance *= 1 + getUtilityArtifactBonus('blueprint-find-bonus', state.character) / 100;
    }

    const cascadeRoll = Math.random();
    // Guarded: the message re-calls getRarityDropChance(), so building it
    // unconditionally would cost a lookup per cascade roll.
    if (isDebug(CH.loot)) {
      debug(CH.loot, `Cascade from ${rarity} to ${nextRarity}: baseChance=${(getRarityDropChance(nextRarity, tier, room.type)*100).toFixed(4)}%, multiplier=${cascadeMultiplier}, finalChance=${(cascadeChance*100).toFixed(4)}%, roll=${(cascadeRoll*100).toFixed(4)}%, triggers=${cascadeRoll < cascadeChance}`);
    }

    if (cascadeRoll < cascadeChance) {
      if (typeof document != 'undefined') {
        setTimeout(() => {
          addNotification('🎰 Extra bonus loot discovered!', 'success');
          processBonusLootEvent(state, nextRarity);
        }, 500);
      }
      else {
        addNotification('🎰 Extra bonus loot discovered!', 'success');
        processBonusLootEvent(state, nextRarity);
      }
    }
  }
}

// ============================================================================
// FISHING EVENT (WATER ROOMS)
// ============================================================================

/**
 * Initialize fishing event for water rooms
 * Sets up fishing state and schedules first fish to spawn after 15 seconds
 * @param {Object} room - Room object (type === 'water')
 * @param {Object} state - gameState object
 */
function initializeFishingEvent(room, state) {
  if (!state.currentRaid?.raidInProgress?.events) {
    return;
  }

  state.currentRaid.raidInProgress.events.fishingEvent = {
    active: true,
    targetActive: false,
    nextFishTime: Date.now() + (typeof document !== 'undefined' ? 15000 : 0), // First fish in 15 seconds if in browser
    count: 0,
    element: null,
    roomIndex: state.currentRaid.raidInProgress.currentRoomIndex
  };

  debug(CH.events, 'Fishing event initialized for water room');
}

/**
 * Cleanup fishing event when leaving water room
 * Removes active fish and deactivates fishing
 * @param {Object} state - gameState object
 */
function cleanupFishingEvent(state) {
  const fishingEvent = state.currentRaid?.raidInProgress?.events?.fishingEvent;
  if (!fishingEvent) return;

  if (fishingEvent.element && fishingEvent.element.parentNode) {
    fishingEvent.element.remove();
  }

  fishingEvent.active = false;
  fishingEvent.targetActive = false;
  fishingEvent.element = null;
  fishingEvent.roomIndex = null;
}

/**
 * Award one Fish to the raid loot pool and log it. Shared by the headless
 * auto-catch (spawnFishCore) and the browser click handler (catchFish) so the
 * reward stays identical on both paths.
 * @param {Object} state - gameState object
 * @param {number} roomIndex - Path step index the catch is logged against
 */
export function awardFishLoot(state, roomIndex) {
  const raid = state.currentRaid.raidInProgress;
  addLoot(raid.lootGained, 'Fish', 1);
  addRaidMessage(`🎣 Caught 1x Fish!`, 'success', {
    stepIndex: roomIndex,
    notificationMessage: `🎣 Caught Fish!`
  });
}

/**
 * Core fish auto-catch logic (pure state management, no DOM). Used by the
 * headless simulator and tests; the browser instead spawns a clickable fish
 * (eventSystem.js) and calls catchFish on click.
 * @param {Object} state - gameState object
 * @returns {boolean} True if a fish was caught, false if capped/active/wrong room
 */
export function spawnFishCore(state) {
  const raid = state.currentRaid.raidInProgress;
  const fishingEvent = raid.events.fishingEvent;

  // Don't spawn if already active or room changed
  if (fishingEvent.count >= 5 || fishingEvent.targetActive || fishingEvent.roomIndex !== raid.currentRoomIndex) {
    return false;
  }

  // No browser to click the fish — auto-catch it and award the loot
  awardFishLoot(state, fishingEvent.roomIndex);
  fishingEvent.count++;
  fishingEvent.targetActive = false;
  fishingEvent.nextFishTime = Date.now();

  debug(CH.events, `Fish caught (count: ${fishingEvent.count})`);
  return true;
}

// ============================================================================
// EVENT PRESENTATION (INJECTABLE)
// ============================================================================
// How a bonus-loot / fishing event is presented when it fires. The default
// is the headless auto-grant above, which the 90-day simulator and unit tests
// rely on. In the browser, raid.js calls setEventPresenters() to swap in the
// clickable collectibles from eventSystem.js, so the player must click the
// diamond / fish to collect the reward — the original intended behavior.

let bonusLootPresenter = spawnBonusLootEventCore;
let fishingPresenter = spawnFishCore;

/**
 * Override how bonus-loot / fishing events are presented (browser only).
 * @param {Object} presenters - { bonusLoot?, fishing? } (state) => void
 */
export function setEventPresenters({ bonusLoot, fishing } = {}) {
  if (bonusLoot) bonusLootPresenter = bonusLoot;
  if (fishing) fishingPresenter = fishing;
}

/**
 * Restore the headless auto-grant presenters.
 *
 * Needed because Auto-Raid Protocol turns the clickable collectibles OFF: a
 * hands-off raid cannot click a diamond, so leaving the browser presenters
 * installed would drop that loot on the floor and toast about it. The
 * auto-grant path is the same one the simulator runs, so automated raids
 * collect exactly what a modelled raid collects.
 *
 * Presenters are module state, so this must be called per raid rather than
 * once at boot — the player can toggle automation between raids.
 */
export function resetEventPresenters() {
  bonusLootPresenter = spawnBonusLootEventCore;
  fishingPresenter = spawnFishCore;
}

export function presentBonusLootEvent(state) { return bonusLootPresenter(state); }
export function presentFishingEvent(state) { return fishingPresenter(state); }
